/**
 * Read hooks for the design theme — the panel and the board band derive from
 * the SAME inputs so they can never disagree within a render pass (same rule
 * the comments hooks follow).
 */
import type {
  ArtDirection,
  DesignJobState,
  DesignRefInfo,
  DesignShotInfo,
} from "@kola/shared";
import { readShortsScriptLines, sampleShortsScript } from "@kola/shared";
import { useMemo } from "react";
import { useStore } from "zustand";

import type { BoardSession } from "../../core/session.js";
import { sampleScript } from "./sample.js";

export interface DesignThemeView {
  artDirection: ArtDirection;
  /** Has anyone generated or written a theme yet? Drives the empty state. */
  present: boolean;
  brief: string;
  /** Model that produced the current art direction, if it came from one. */
  model: string | undefined;
  updatedAt: number | undefined;
  /** Style-reference frames, in order. */
  refs: DesignRefInfo[];
  /** Every generated key visual, newest run last. */
  shots: DesignShotInfo[];
  /** Shots of the most recent run — what the board band shows. */
  latestRun: DesignShotInfo[];
  /** Live image-run state, read-only (the server is its only writer). */
  job: DesignJobState | undefined;
}

/** The board's design theme, re-derived on any design write. */
export function useDesignTheme(session: BoardSession): DesignThemeView {
  const designVersion = useStore(session.store, (s) => s.designVersion);
  return useMemo(() => {
    void designVersion; // re-derive on any design change
    const shots = session.board.listDesignShots();
    // The newest run is the one the band shows. Derived from the shots
    // themselves rather than stored, so it cannot disagree with them.
    const latestRunId = shots.length > 0 ? shots[shots.length - 1]!.runId : undefined;
    return {
      artDirection: session.board.getArtDirection(),
      present: session.board.hasArtDirection(),
      brief: session.board.getDesignBrief(),
      model: session.board.getDesignModel(),
      updatedAt: session.board.getDesignUpdatedAt(),
      refs: session.board.listDesignRefs(),
      shots,
      latestRun:
        latestRunId === undefined ? [] : shots.filter((shot) => shot.runId === latestRunId),
      job: session.board.getDesignJob(),
    };
  }, [session, designVersion]);
}

export interface ScriptSampleView {
  /** The sample that would be sent, already within the server's caps. */
  sample: string[];
  /** Non-empty Original Script rows on the board — what the sample came from. */
  totalLines: number;
}

/**
 * Sample the Original Script column (F-28) for the generator.
 *
 * Deliberately NOT memoized on a cell-content version: this is read at the
 * moment the user presses Generate, and it walks up to 50,000 rows. Recomputing
 * it on every keystroke anywhere in the grid would be a real cost for a value
 * nothing renders. The caller invokes it in the click handler.
 */
export function readScriptSample(session: BoardSession): ScriptSampleView {
  const sheetId = session.store.getState().sheetId;
  if (sheetId === null) return { sample: [], totalLines: 0 };

  const colId = session.board.sourceColumnId(sheetId);
  if (colId === undefined) return { sample: [], totalLines: 0 };

  const lines: string[] = [];
  for (const rowId of session.board.sortedRowIds(sheetId)) {
    const text = session.board.getCellText(sheetId, rowId, colId).trim();
    if (text.length > 0) lines.push(text);
  }
  // F-142: a week of Shorts is five scripts — each short's opening lines
  // under its own name, the same cut the server makes for cover frames.
  const shorts = readShortsScriptLines(session.board, sheetId);
  if (shorts.length > 0) return { sample: sampleShortsScript(shorts), totalLines: lines.length };
  return { sample: sampleScript(lines), totalLines: lines.length };
}
